import { useState, useEffect } from 'react';
import { FileText, Calendar, CalendarClock, CheckCircle, Clock, Trash2 } from 'lucide-react';
import { Card } from '../ui/Card';
import { Loading } from '../ui/Loading';
import { Alert } from '../ui/Alert';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { projectService } from '../../services/api';

interface ArticlesListProps {
  projectId: string;
  onViewArticle: (article: any) => void;
}

export function ArticlesList({ projectId, onViewArticle }: ArticlesListProps) {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [activeTab, setActiveTab] = useState<'published' | 'draft'>('published');

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<number | null>(null);

  useEffect(() => {
    loadArticles();
  }, [projectId]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectService.getProjectArticles(projectId);

      setArticles(data);
    } catch (err: any) {
      setError('Error al cargar los artículos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Fecha no disponible';

    const date = new Date(dateString);

    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      return 'Fecha inválida';
    }

    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatWordCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const checkBoolean = (val: any) => {
    if (!val) return false;
    if (val === true || val === 1) return true;
    const s = String(val).toLowerCase().trim();
    // Check for explicit 'no' or 'false'
    if (s === 'no' || s === 'false' || s === '0') return false;
    // Otherwise if it has any overlapping valid value
    return s === 'si' || s === 'yes' || s === 'true' || s === '1' || s.includes('si') || s.includes('yes');
  };


  const handleScheduleSave = async () => {
    if (!scheduleDate || !scheduleTime) {
      alert('Por favor, selecciona fecha y hora');
      return;
    }

    if (!selectedArticleId) {
      alert('❌ Error: No se seleccionó ningún artículo');
      return;
    }

    try {
      const scheduledDateTime = `${scheduleDate} ${scheduleTime}:00`;

      // Pasar el articleId en lugar del projectId
      await projectService.updateArticleSchedule(selectedArticleId, scheduledDateTime);

      alert(`✅ Artículo programado exitosamente para ${scheduleDate} a las ${scheduleTime}`);
      setShowScheduleModal(false);
      setScheduleDate('');
      setScheduleTime('');
      setSelectedArticleId(null);

      // Recargar la lista de artículos
      await loadArticles();
    } catch (error) {
      alert('❌ Error al programar el artículo. Por favor, intenta de nuevo.');
    }
  };

  const handleDeleteClick = (articleId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setArticleToDelete(articleId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!articleToDelete) return;

    try {
      await projectService.deleteArticle(articleToDelete);
      alert('✅ Artículo eliminado correctamente');
      loadArticles();
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Error al eliminar el artículo.';
      alert(`❌ ${msg}`);
    } finally {
      setShowDeleteModal(false);
      setArticleToDelete(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex justify-center">
          <Loading size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert type="error" title="Error">
        {error}
      </Alert>
    );
  }

  if (articles.length === 0) {
    return (
      <Card className="p-8 text-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay artículos generados
            </h3>
            <p className="text-gray-600">
              Genera tu primer artículo para este proyecto
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Filter articles based on active tab


  // ...



  // STRICT logic as requested by user
  // Checks only subid_wordpress for 'si', 'yes', 'true', '1'
  const isArticlePublished = (article: any) => {
    const val = article.subid_wordpress;
    if (!val) return false;
    const s = String(val).toLowerCase().trim();
    return s === 'si' || s === 'yes' || s === 'true' || s === '1';
  };

  // Filter articles based on active tab
  const publishedArticles = articles
    .filter(article => isArticlePublished(article))
    .sort((a, b) => {
      const dateA = a.fecha_programado ? new Date(a.fecha_programado).getTime() : 0;
      const dateB = b.fecha_programado ? new Date(b.fecha_programado).getTime() : 0;
      return dateB - dateA;
    });

  const draftArticles = articles
    .filter(article => !isArticlePublished(article))
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Default sort by creation
    });

  const currentList = activeTab === 'published' ? publishedArticles : draftArticles;

  return (
    <Card className="overflow-hidden bg-white shadow-sm border border-gray-200">
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('published')}
            className={`flex-1 py-4 text-center text-sm font-medium border-b-2 transition-colors ${activeTab === 'published'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            Subido a WordPress ({publishedArticles.length})
          </button>
          <button
            onClick={() => setActiveTab('draft')}
            className={`flex-1 py-4 text-center text-sm font-medium border-b-2 transition-colors ${activeTab === 'draft'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            Sin Procesar ({draftArticles.length})
          </button>
        </div>
      </div>



      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Título
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Fecha Creación
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Fecha Programada
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Palabras
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Imágenes
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentList.map((article) => {

              const articleData = article.article_data;
              const rawTitle = article.title ?? articleData?.title ?? 'Sin título';
              const title =
                typeof rawTitle === 'string'
                  ? rawTitle
                  : (
                    rawTitle?.title ||
                    rawTitle?.h1 ||
                    rawTitle?.meta?.title ||
                    rawTitle?.mainKeyword ||
                    'Sin título'
                  );
              const wordCount = article.word_count || articleData?.wordCount || 0;
              const imagesCount = Array.isArray(articleData?.images) ? articleData.images.length : 0;
              const createdAt = article.created_at;
              const fechaProgramado = article.fecha_programado;

              // Use STRICT logic for the status badge: only show "Publicado" if actual field explicitly says so
              const published = checkBoolean(article.publicado) && String(article.publicado).toLowerCase().trim() !== 'no';

              return (
                <tr
                  key={article.id || article.session_id}
                  className="hover:bg-gray-50 transition-colors group"
                >
                  <td
                    className="px-6 py-4 cursor-pointer"
                    onClick={() => onViewArticle(article)}
                  >
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                      {title}
                    </div>
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onViewArticle(article)}
                  >
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      <span className="hidden lg:inline">{formatDate(createdAt)}</span>
                      <span className="lg:hidden">{new Date(createdAt).toLocaleDateString('es-ES')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {fechaProgramado ? (
                      <div className="flex items-center text-sm text-gray-700">
                        <CalendarClock className="w-4 h-4 mr-1.5 flex-shrink-0 text-blue-600" />
                        <span className="hidden lg:inline">{formatDate(fechaProgramado)}</span>
                        <span className="lg:hidden">{new Date(fechaProgramado).toLocaleDateString('es-ES')}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Sin programar</span>
                    )}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onViewArticle(article)}
                  >
                    <div className="text-sm text-gray-900 font-medium">
                      {formatWordCount(wordCount)}
                    </div>
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onViewArticle(article)}
                  >
                    <div className="text-sm text-gray-900 font-medium">
                      {imagesCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      {published ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="w-4 h-4 mr-1" /> Publicado
                        </span>
                      ) : checkBoolean(article.subid_wordpress) && article.fecha_programado && new Date(article.fecha_programado) > new Date() ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          <Clock className="w-4 h-4 mr-1" /> Programado (WP)
                        </span>
                      ) : checkBoolean(article.subid_wordpress) ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                          <CheckCircle className="w-4 h-4 mr-1" /> Subido (WP)
                        </span>
                      ) : fechaProgramado ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          <Clock className="w-4 h-4 mr-1" /> Programado
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          <Clock className="w-4 h-4 mr-1" /> Pendiente
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">


                    <button
                      onClick={(e) => handleDeleteClick(article.id, e)}
                      disabled={checkBoolean(article.subid_wordpress)}
                      className={`inline-flex items-center justify-center p-1.5 ml-2 rounded-md transition-colors ${checkBoolean(article.subid_wordpress)
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                      title={checkBoolean(article.subid_wordpress) ? "No se puede eliminar porque ya está en WordPress" : "Eliminar artículo"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Artículo"
        variant="danger"
        maxWidth="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Eliminar
            </Button>
          </>
        }
      >
        <p>
          ¿Estás seguro de que deseas eliminar este artículo de la base de datos?
          <br />
          <span className="font-bold text-red-600">Esta acción no se puede deshacer.</span>
        </p>
      </Modal>

      {/* Modal de Programación */}
      {showScheduleModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-4"
          onClick={() => setShowScheduleModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-2xl max-h-[calc(100vh-2rem)] md:max-h-[calc(100dvh-4rem)] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 border-b border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Programar Artículo</h2>
                  <p className="text-red-100 text-sm mt-1">
                    Selecciona fecha y hora para publicar
                  </p>
                </div>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="text-white hover:text-red-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Fecha */}
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Fecha de Publicación <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-500 focus:outline-none text-base"
                  required
                />
              </div>

              {/* Hora */}
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Hora de Publicación <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-500 focus:outline-none text-base"
                  required
                />
              </div>

              {/* Info */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>📅 Nota:</strong> El artículo se publicará automáticamente en la fecha y hora seleccionadas.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleScheduleSave}
                className="rounded-lg bg-red-600 text-white px-6 py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all hover:bg-red-700"
              >
                🕐 Programar
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

